import { gql } from "@apollo/client";

/**
 * SHELV — centralized GraphQL.
 *
 * Every query and mutation this module uses lives here. Never inline `gql` in a
 * page (module-build standard § Centralized queries.ts).
 *
 * Operation names are Shelv-prefixed because graphql-codegen requires
 * globally-unique operation names and Module Sync copies module folders
 * verbatim into other tenants.
 */

// ── Fragments ────────────────────────────────────────────────────────────

export const SHELV_RECIPE_CARD_FIELDS = gql`
  fragment ShelvRecipeCardFields on ShelvRecipe {
    id
    name
    description
    servings
    prepTime
    cookTime
    sessionType
    dishType
    imageCid
    imageUrl
    isActive
    # Drives RecipeCard's activity dots. The component has always taken this
    # prop; until 2026-09-05 nothing supplied it, so every card read zero.
    timesCooked
    inferredTag
  }
`;

// ── Kitchens ─────────────────────────────────────────────────────────────

export const GET_MY_KITCHENS = gql`
  query ShelvMyKitchens {
    myKitchens {
      id
      name
      description
      tier
      timezone
      logoCid
      logoUrl
      memberCount
      captureCount
      ownerClientId
      isPersonalShelf
      members {
        clientId
        role
        displayName
        sections
        joinedAt
      }
    }
  }
`;

/**
 * The shelf a person always has.
 *
 * Idempotent and safe on every land — the server holds a unique index that makes
 * concurrent calls resolve to one shelf rather than two. Decided at the
 * 2026-08-28 founders meeting: a chef does not create a shelf, a shelf is what
 * an account IS.
 */
/**
 * Who the caller is, for the Pro gate. Permissions come from `myClient` — a live
 * query — NOT from the JWT, so a grant takes effect without signing out again.
 */
export const GET_MY_SHELV_ACCESS = gql`
  query ShelvMyAccess {
    myClient {
      id
      permissions
    }
  }
`;

export const ENSURE_MY_SHELF = gql`
  mutation ShelvEnsureMyShelf {
    ensureMyShelf {
      id
      name
      description
      tier
      timezone
      logoCid
      logoUrl
      memberCount
      captureCount
      ownerClientId
      isPersonalShelf
      members {
        clientId
        role
        displayName
        sections
        joinedAt
      }
    }
  }
`;

export const CREATE_KITCHEN = gql`
  mutation ShelvCreateKitchen($input: ShelvCreateKitchenInput!) {
    createKitchen(input: $input) {
      id
      name
      tier
      timezone
    }
  }
`;

export const UPDATE_KITCHEN = gql`
  mutation ShelvUpdateKitchen($id: ID!, $input: ShelvUpdateKitchenInput!) {
    updateKitchen(id: $id, input: $input) {
      id
      name
      description
      timezone
      logoCid
    }
  }
`;

export const ADD_KITCHEN_MEMBER = gql`
  mutation ShelvAddKitchenMember($kitchenId: ID!, $input: KitchenMemberInput!) {
    addKitchenMember(kitchenId: $kitchenId, input: $input) {
      id
      memberCount
      members {
        clientId
        role
        displayName
        sections
      }
    }
  }
`;

export const UPDATE_KITCHEN_MEMBER = gql`
  mutation ShelvUpdateKitchenMember($kitchenId: ID!, $input: KitchenMemberInput!) {
    updateKitchenMember(kitchenId: $kitchenId, input: $input) {
      id
      members {
        clientId
        role
        displayName
        sections
      }
    }
  }
`;

export const REMOVE_KITCHEN_MEMBER = gql`
  mutation ShelvRemoveKitchenMember($kitchenId: ID!, $clientId: ID!) {
    removeKitchenMember(kitchenId: $kitchenId, clientId: $clientId) {
      id
      memberCount
      members {
        clientId
        role
        displayName
      }
    }
  }
`;

// ── The recipe book ──────────────────────────────────────────────────────

export const GET_SHELV_RECIPES = gql`
  query ShelvRecipes {
    shelvRecipes {
      ...ShelvRecipeCardFields
    }
  }
  ${SHELV_RECIPE_CARD_FIELDS}
`;

export const GET_SHELV_RECIPE = gql`
  query ShelvRecipeDetail($id: ID!) {
    shelvRecipe(id: $id) {
      ...ShelvRecipeCardFields
      instructions
      notes
      caloriesPerServing
      createdAt
      updatedAt
      lastCookedAt
      ingredients {
        id
        quantity
        unit
        notes
        isOptional
        ingredient { id name }
      }
      # Null on an original. canViewOriginal is computed SERVER-side; the page
      # HIDES the link when it is false rather than disabling it, because a
      # disabled link still discloses that the recipe exists on another shelf.
      provenance {
        rootRecipeId
        originalOwnerName
        forkGeneration
        canViewOriginal
      }
    }
  }
  ${SHELV_RECIPE_CARD_FIELDS}
`;

export const UPDATE_SHELV_RECIPE = gql`
  mutation ShelvUpdateRecipeDetail($id: ID!, $input: ShelvUpdateRecipeInput!) {
    shelvUpdateRecipe(id: $id, input: $input) {
      ...ShelvRecipeCardFields
      instructions
      notes
    }
  }
  ${SHELV_RECIPE_CARD_FIELDS}
`;

export const DELETE_SHELV_RECIPE = gql`
  mutation ShelvDeleteRecipeDetail($id: ID!) {
    shelvDeleteRecipe(id: $id)
  }
`;

// ── Share, fork and cook signals ─────────────────────────────────────────

/**
 * Share a recipe with one named person. There is no public index and never
 * will be — the 2026-08-28 founders meeting was unambiguous about that.
 *
 * The result says which of the two things actually happened: a fork onto an
 * existing shelf, or an invite that carries the recipe. Never optimistic —
 * the page reports what the server confirmed.
 */
export const SHARE_SHELV_RECIPE = gql`
  mutation ShelvShareRecipeToPerson($id: ID!, $email: String!, $message: String) {
    shelvShareRecipe(id: $id, email: $email, message: $message) {
      delivered
      invited
      forkedRecipeId
      message
    }
  }
`;

/** Take a copy of a recipe you can already see onto your own shelf. */
export const FORK_SHELV_RECIPE = gql`
  mutation ShelvForkRecipeToMyShelf($id: ID!) {
    shelvForkRecipe(id: $id) {
      ...ShelvRecipeCardFields
    }
  }
  ${SHELV_RECIPE_CARD_FIELDS}
`;

/**
 * "Cooked it" — one tap, never a form.
 *
 * TAP is the chef confirming. PROBABLE is the page reporting that the recipe
 * sat open on the bench; the server decides whether that is believable and
 * NEVER lets it move `timesCooked`.
 */
export const MARK_SHELV_RECIPE_COOKED = gql`
  mutation ShelvMarkRecipeCooked($id: ID!, $source: ShelvCookSource, $dwellSeconds: Int) {
    shelvMarkCooked(id: $id, source: $source, dwellSeconds: $dwellSeconds) {
      recorded
      timesCooked
      reason
    }
  }
`;

/**
 * The auto-tag roll-up, fired by OPENING THE SHELF.
 *
 * Page-driven by policy — this platform runs no background timers in admin
 * tooling, and doing it on land keeps the scan correctly kitchen-scoped.
 */
export const ROLL_UP_SHELV_TAGS = gql`
  mutation ShelvRollUpTags($kitchenId: ID) {
    shelvRollUpInferredTags(kitchenId: $kitchenId)
  }
`;

// ── Capture — the free tier's hero ───────────────────────────────────────

export const GET_CAPTURE_ALLOWANCE = gql`
  query ShelvCaptureAllowanceQuery($kitchenId: ID) {
    shelvCaptureAllowance(kitchenId: $kitchenId) {
      used
      limit
      remaining
      unlimited
      monthKey
    }
  }
`;

// ── Capture jobs (2026-09-15) ────────────────────────────────────────────
//
// A capture is a JOB the shelf watches, not a request the chef waits on.
// `startRecipeCapture` returns at once; the shelf polls `shelvCaptureJobs`
// while any job is running; the review page opens `shelvCaptureJob`.
// `text` and the extracted text are deliberately NOT selectable — the shelf
// polls this list every few seconds and a paste can be 16k characters.

export const SHELV_CAPTURE_JOB_FIELDS = gql`
  fragment ShelvCaptureJobFields on ShelvCaptureJob {
    id
    kitchenId
    sourceKind
    status
    url
    fileCid
    fileMimeType
    fileName
    sourceTitle
    extractionMethod
    extractedChars
    error
    stepStartedAt
    finishedAt
    recipeId
    attempts
    createdAt
    updatedAt
    proposal {
      name
      description
      servings
      prepTime
      cookTime
      instructions
      sessionType
      dishType
      dietaryTags
      caloriesPerServing
      notes
      sourceUrl
      unreadable
      rawNotes
      ingredients {
        ingredientName
        category
        quantity
        unit
        notes
        isOptional
      }
    }
  }
`;

export const START_RECIPE_CAPTURE = gql`
  mutation ShelvStartRecipeCapture($input: StartRecipeCaptureInput!) {
    startRecipeCapture(input: $input) {
      ...ShelvCaptureJobFields
    }
  }
  ${SHELV_CAPTURE_JOB_FIELDS}
`;

export const GET_SHELV_CAPTURE_JOBS = gql`
  query ShelvCaptureJobs($kitchenId: ID) {
    shelvCaptureJobs(kitchenId: $kitchenId) {
      ...ShelvCaptureJobFields
    }
  }
  ${SHELV_CAPTURE_JOB_FIELDS}
`;

export const GET_SHELV_CAPTURE_JOB = gql`
  query ShelvCaptureJob($id: ID!) {
    shelvCaptureJob(id: $id) {
      ...ShelvCaptureJobFields
    }
  }
  ${SHELV_CAPTURE_JOB_FIELDS}
`;

export const RETRY_RECIPE_CAPTURE = gql`
  mutation ShelvRetryRecipeCapture($id: ID!) {
    retryRecipeCapture(id: $id) {
      ...ShelvCaptureJobFields
    }
  }
  ${SHELV_CAPTURE_JOB_FIELDS}
`;

export const DISCARD_RECIPE_CAPTURE = gql`
  mutation ShelvDiscardRecipeCapture($id: ID!) {
    discardRecipeCapture(id: $id)
  }
`;

// ── AI settings (admin) ──────────────────────────────────────────────────
// Secrets are write-only: the read carries a four-character hint, never a value.

export const SHELV_AI_SETTINGS_FIELDS = gql`
  fragment ShelvAiSettingsFields on ShelvAiSettings {
    localAi { configured source baseUrl secretHint model }
    knowledgeFs { configured source baseUrl secretHint }
    harness { configured source baseUrl secretHint }
  }
`;

export const GET_SHELV_AI_SETTINGS = gql`
  query ShelvAiSettings {
    shelvAiSettings {
      ...ShelvAiSettingsFields
    }
  }
  ${SHELV_AI_SETTINGS_FIELDS}
`;

export const UPDATE_SHELV_AI_SETTINGS = gql`
  mutation ShelvUpdateAiSettings($input: UpdateShelvAiSettingsInput!) {
    updateShelvAiSettings(input: $input) {
      ...ShelvAiSettingsFields
    }
  }
  ${SHELV_AI_SETTINGS_FIELDS}
`;

export const CHECK_SHELV_AI_SETTINGS = gql`
  query ShelvAiSettingsCheck {
    shelvAiSettingsCheck {
      name
      configured
      source
      reachable
      httpStatus
      message
    }
  }
`;

export const SAVE_CAPTURED_RECIPE = gql`
  mutation ShelvSaveCapturedRecipe($input: ShelvSaveCapturedRecipeInput!) {
    saveCapturedRecipe(input: $input)
  }
`;

// ── Chunked IPFS upload (module-local) ───────────────────────────────────
//
// Operation names are Shelv-prefixed because graphql-codegen requires globally
// unique operation names and Module Sync copies module folders verbatim.
// The result's `ipfsHash` IS the CID — Shelv stores that, never the `url`.

export const SHELV_INITIATE_UPLOAD = gql`
  mutation ShelvInitiateUpload($fileName: String!, $totalChunks: Float!, $fileSize: Float!) {
    mediaCDNInitiateUpload(fileName: $fileName, totalChunks: $totalChunks, fileSize: $fileSize)
  }
`;

export const SHELV_UPLOAD_CHUNK = gql`
  mutation ShelvUploadChunk($uploadId: String!, $chunkNumber: Float!, $chunk: Upload!) {
    mediaCDNUploadChunk(uploadId: $uploadId, chunkNumber: $chunkNumber, chunk: $chunk)
  }
`;

export const SHELV_COMPLETE_UPLOAD = gql`
  mutation ShelvCompleteUpload($uploadId: String!) {
    mediaCDNCompleteUpload(uploadId: $uploadId) {
      ipfsHash
      url
      fileSize
      mimeType
      fileName
    }
  }
`;

export const SHELV_CANCEL_UPLOAD = gql`
  mutation ShelvCancelUpload($uploadId: String!) {
    mediaCDNCancelUpload(uploadId: $uploadId)
  }
`;

export const SHELV_GET_UPLOAD_PROGRESS = gql`
  mutation ShelvGetUploadProgress($uploadId: String!) {
    mediaCDNGetUploadProgress(uploadId: $uploadId)
  }
`;

// ═══════════════════════════════════════════════════════════════════════════
// SHELV PRO
//
// Everything below is behind `ProGate` on the client and `shelvPermissions.pro`
// on the server. The server is the gate that matters — the client one decides
// which story a person is told, not what they can reach.
//
// Operation names stay `Shelv`-prefixed: codegen needs them globally unique and
// Module Sync copies this folder verbatim into other tenants.
// ═══════════════════════════════════════════════════════════════════════════

/** The week's service plan. Dates are Brisbane day keys — see utils/mealDate.ts. */
export const GET_SHELV_MEAL_PLANS_RANGE = gql`
  query ShelvMealPlansRange($startDate: String!, $endDate: String!) {
    shelvMealPlansByDateRange(startDate: $startDate, endDate: $endDate) {
      id
      mealDate
      mealType
      servings
      sortOrder
      specialNotes
      quickDescription
      recipeId
      recipe {
        id
        name
        imageCid
        prepTime
        cookTime
      }
      servicePhotos {
        cid
        url
        uploadedAt
      }
    }
  }
`;

export const CREATE_SHELV_MEAL_PLAN = gql`
  mutation ShelvCreateMealPlanEntry($input: ShelvCreateMealPlanInput!) {
    shelvCreateMealPlan(input: $input) {
      id
      mealDate
      mealType
      servings
      recipeId
      quickDescription
      recipe {
        id
        name
        imageCid
      }
    }
  }
`;

export const COPY_SHELV_MENU = gql`
  mutation ShelvCopyMenu($sourceStartDate: String!, $sourceEndDate: String!, $targetStartDate: String!) {
    shelvCopyMealPlanDateRange(sourceStartDate: $sourceStartDate, sourceEndDate: $sourceEndDate, targetStartDate: $targetStartDate, replace: false) { id }
  }
`;

export const UPDATE_SHELV_MEAL_PLAN = gql`
  mutation ShelvUpdateMealPlanEntry($id: ID!, $input: ShelvUpdateMealPlanInput!) {
    shelvUpdateMealPlan(id: $id, input: $input) {
      id
      servings
      specialNotes
      mealType
      mealDate
    }
  }
`;

export const DELETE_SHELV_MEAL_PLAN = gql`
  mutation ShelvDeleteMealPlanEntry($id: ID!) {
    shelvDeleteMealPlan(id: $id)
  }
`;

/** Budget. `period` is a ShelvBudgetPeriod: DAY | WEEK | MONTH | FY | CUSTOM. */
export const GET_SHELV_BUDGET_SUMMARY = gql`
  query ShelvBudgetSummary($period: ShelvBudgetPeriod!, $anchorDate: String) {
    shelvFoodBudgetSummary(period: $period, anchorDate: $anchorDate) {
      period
      startDate
      endDate
      periodBudget
      spent
      remaining
      percentUsed
      spentFromOrders
      spentFromInvoices
      estimatedSpend
      estimatedRemaining
      estimatedPercentUsed
    }
  }
`;

export const GET_SHELV_SPEND_BREAKDOWN = gql`
  query ShelvSpendBreakdown($period: ShelvBudgetPeriod!, $anchorDate: String) {
    shelvFoodSpendByCategory(period: $period, anchorDate: $anchorDate) {
      category
      spent
      estimated
      percent
    }
    shelvFoodSpendBySupplier(period: $period, anchorDate: $anchorDate) {
      supplierId
      supplierName
      spent
      estimated
      percent
    }
  }
`;

export const GET_SHELV_FOOD_BUDGET = gql`
  query ShelvFoodBudgetSettings {
    shelvFoodBudget {
      id
      monthlyBudget
      currency
      financialYearStartMonth
    }
  }
`;

export const UPDATE_SHELV_FOOD_BUDGET = gql`
  mutation ShelvUpdateFoodBudgetSettings($input: ShelvUpdateFoodBudgetInput!) {
    shelvUpdateFoodBudget(input: $input) {
      id
      monthlyBudget
      currency
    }
  }
`;

/** What the plan needs bought — the bridge from calendar to order. */
export const GET_SHELV_AGGREGATED_INGREDIENTS = gql`
  query ShelvAggregatedIngredients($startDate: String!, $endDate: String!) {
    shelvAggregatedIngredientsForDateRange(startDate: $startDate, endDate: $endDate) {
      ingredientId
      ingredientName
      totalQuantity
      unit
      supplierId
      supplierName
      estimatedCost
      unitCost
      mealPlanCount
    }
  }
`;

export const GET_SHELV_SUPPLIER_ORDERS = gql`
  query ShelvSupplierOrdersList {
    shelvSupplierOrders {
      id
      orderNumber
      status
      orderDate
      expectedDelivery
      actualDelivery
      totalAmount
      itemCount
      notes
      hasDiscrepancies
      supplier {
        id
        name
        contactEmail
      }
    }
  }
`;

export const GENERATE_SHELV_ORDERS = gql`
  mutation ShelvGenerateOrders($input: ShelvGenerateOrdersInput!) {
    shelvGenerateOrdersFromMealPlans(input: $input) {
      id
      orderNumber
      status
      totalAmount
      itemCount
      supplier {
        id
        name
      }
    }
  }
`;

export const GET_SHELV_MEAL_FEEDBACKS = gql`
  query ShelvMealFeedbackList {
    shelvMealFeedbacks {
      id
      rating
      comments
      feedbackType
      isResolved
      reportedBy
      createdAt
      photos {
        cid
        url
      }
      mealPlan {
        id
        mealDate
        mealType
        servings
        recipe {
          id
          name
        }
      }
    }
  }
`;

export const RESOLVE_SHELV_FEEDBACK = gql`
  mutation ShelvResolveFeedbackEntry($id: ID!, $resolutionNotes: String) {
    shelvResolveFeedback(id: $id, resolutionNotes: $resolutionNotes) {
      id
      isResolved
      resolutionNotes
      resolvedAt
    }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════
// INVITES — the growth loop
// ═══════════════════════════════════════════════════════════════════════════

const INVITE_FIELDS = `
  id
  slug
  email
  fName
  lName
  status
  accessMode
  couponCode
  kitchenId
  createdByName
  invitedByClientId
  emailSentAt
  accessedAt
  activatedAt
  firstRecipeAddedAt
  lastChasedAt
  expiresAt
  createdAt
`;

export const GET_SHELV_INVITES = gql`
  query ShelvInvitesList { shelvInvites { ${INVITE_FIELDS} } }
`;

export const GET_MY_SHELV_INVITES = gql`
  query ShelvMyInvites { myShelvInvites { ${INVITE_FIELDS} } }
`;

export const CREATE_SHELV_INVITE = gql`
  mutation ShelvCreateInvite($input: CreateShelvInviteInput!) {
    createShelvInvite(input: $input) { ${INVITE_FIELDS} }
  }
`;

export const CREATE_SHELV_MEMBER_INVITE = gql`
  mutation ShelvCreateMemberInvite($input: CreateShelvMemberInviteInput!) {
    createShelvMemberInvite(input: $input) { ${INVITE_FIELDS} }
  }
`;

export const CHASE_SHELV_INVITE = gql`
  mutation ShelvChaseInvite($id: ID!, $message: String) {
    chaseShelvInvite(id: $id, message: $message) { id lastChasedAt }
  }
`;

export const DELETE_SHELV_INVITE = gql`
  mutation ShelvDeleteInvite($id: ID!) { deleteShelvInvite(id: $id) }
`;

export const GET_SHELV_INVITE_GRAPH = gql`
  query ShelvInviteGraphQuery {
    shelvInviteGraph {
      id
      email
      displayName
      status
      invitedByClientId
      invitedByName
      canViewDetail
      detailUrl
    }
  }
`;

/** Public — the landing page. Narrow on purpose; never widen it. */
export const GET_PUBLIC_SHELV_INVITE = gql`
  query ShelvPublicInvite($slug: String!) {
    publicShelvInvite(slug: $slug) {
      slug
      fName
      kitchenName
      invitedByName
      accessMode
      status
      isExpired
      # Set when a recipe was shared with an address that has no account yet.
      # The NAME only — the public page can say what is waiting, and cannot
      # read it.
      sharedRecipeName
    }
  }
`;

export const CLAIM_SHELV_INVITE = gql`
  mutation ShelvClaimInvite($slug: String!, $fName: String, $lName: String) {
    claimShelvInvite(slug: $slug, fName: $fName, lName: $lName) {
      success
      token
      clientId
      kitchenId
      message
      # TRUE when the address already has an account here. The server
      # deliberately mints no session in that case (a slug is not proof you are
      # the addressee), so the page must send them to sign in rather than
      # showing a dead end. acceptShelvInvite is the authenticated finish.
      requiresLogin
    }
  }
`;

/**
 * The AUTHENTICATED finish for an invite whose address already had an account.
 *
 * Matched server-side against the signed-in caller's OWN client record, never
 * against an email the page supplies — someone holding a slug addressed to
 * another person is simply not that person.
 */
export const ACCEPT_SHELV_INVITE = gql`
  mutation ShelvAcceptInvite($slug: String!) {
    acceptShelvInvite(slug: $slug) {
      success
      clientId
      kitchenId
      message
      requiresLogin
    }
  }
`;
